import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaBlog, FaEnvelope, FaUsers, FaEye, FaPlus,
  FaChartLine, FaCheck, FaCalendar
} from 'react-icons/fa';
import { blogsApi, contactsApi, usersApi } from '../../services/api';
import { AdminLayout } from '../../components/admin';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBlogs: 0,
    publishedBlogs: 0,
    draftBlogs: 0,
    totalContacts: 0,
    unreadContacts: 0,
    todayContacts: 0,
    totalUsers: 0,
    activeUsers: 0
  });
  const [recentBlogs, setRecentBlogs] = useState([]);
  const [recentContacts, setRecentContacts] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [blogsRes, contactsRes, contactStatsRes, usersStatsRes] = await Promise.all([
        blogsApi.getAll(1, 5),
        contactsApi.getAll(1, 5),
        contactsApi.getStats(),
        usersApi.getStats()
      ]);

      // Process blogs
      if (blogsRes.success) {
        setRecentBlogs(blogsRes.data || []);
        // Get all blogs for stats
        const allBlogsRes = await blogsApi.getAll(1, 1000);
        if (allBlogsRes.success) {
          const allBlogs = allBlogsRes.data || [];
          const published = allBlogs.filter(b => b.status === 'published').length;
          setStats(prev => ({
            ...prev,
            totalBlogs: allBlogs.length,
            publishedBlogs: published,
            draftBlogs: allBlogs.length - published
          }));
        }
      }

      // Process contacts
      if (contactsRes.success) {
        setRecentContacts(contactsRes.data || []);
      }

      if (contactStatsRes.success) {
        setStats(prev => ({
          ...prev,
          totalContacts: contactStatsRes.data.stats.total,
          unreadContacts: contactStatsRes.data.stats.unread,
          todayContacts: contactStatsRes.data.stats.today
        }));
      }

      // Process users
      if (usersStatsRes.success) {
        setStats(prev => ({
          ...prev,
          totalUsers: usersStatsRes.data.stats.total,
          activeUsers: usersStatsRes.data.stats.active
        }));
      }
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-dark-800">Dashboard</h1>
            <p className="text-dark-500 mt-1">Welcome back! Here's what's happening.</p>
          </div>
          <Link
            to="/admin/blogs/new"
            className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <FaPlus />
            <span>New Post</span>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            className="bg-white rounded-xl shadow-sm p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-500 text-sm">Total Posts</p>
                <p className="text-2xl font-bold text-dark-800">{stats.totalBlogs}</p>
                <p className="text-xs text-green-600 mt-1">
                  {stats.publishedBlogs} published
                </p>
              </div>
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <FaBlog className="w-6 h-6 text-primary-500" />
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-500 text-sm">Messages</p>
                <p className="text-2xl font-bold text-dark-800">{stats.totalContacts}</p>
                {stats.unreadContacts > 0 && (
                  <p className="text-xs text-red-600 mt-1">
                    {stats.unreadContacts} unread
                  </p>
                )}
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center relative">
                <FaEnvelope className="w-6 h-6 text-blue-500" />
                {stats.unreadContacts > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                    {stats.unreadContacts > 9 ? '9+' : stats.unreadContacts}
                  </span>
                )}
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-500 text-sm">Users</p>
                <p className="text-2xl font-bold text-dark-800">{stats.totalUsers}</p>
                <p className="text-xs text-green-600 mt-1">
                  {stats.activeUsers} active
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <FaUsers className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-500 text-sm">Today</p>
                <p className="text-2xl font-bold text-dark-800">{stats.todayContacts}</p>
                <p className="text-xs text-dark-400 mt-1">
                  new messages
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <FaCalendar className="w-6 h-6 text-green-500" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Blogs */}
          <motion.div
            className="bg-white rounded-xl shadow-sm overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-semibold text-dark-800">Recent Posts</h2>
              <Link
                to="/admin/blogs"
                className="text-sm text-primary-500 hover:text-primary-600"
              >
                View All
              </Link>
            </div>
            {recentBlogs.length === 0 ? (
              <div className="p-6 text-center text-dark-500">
                No blog posts yet
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {recentBlogs.map((blog) => (
                  <Link
                    key={blog.id}
                    to={`/admin/blogs/edit/${blog.id}`}
                    className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-dark-800 truncate">{blog.title}</p>
                      <div className="flex items-center space-x-3 mt-1">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          blog.status === 'published'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {blog.status}
                        </span>
                        <span className="text-xs text-dark-400 flex items-center">
                          <FaEye className="mr-1" /> {blog.views || 0}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs text-dark-400 ml-4">
                      {formatDate(blog.created_at)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </motion.div>

          {/* Recent Contacts */}
          <motion.div
            className="bg-white rounded-xl shadow-sm overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-semibold text-dark-800">Recent Messages</h2>
              <Link
                to="/admin/contacts"
                className="text-sm text-primary-500 hover:text-primary-600"
              >
                View All
              </Link>
            </div>
            {recentContacts.length === 0 ? (
              <div className="p-6 text-center text-dark-500">
                No messages yet
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {recentContacts.map((contact) => (
                  <Link
                    key={contact.id}
                    to="/admin/contacts"
                    className={`flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors ${
                      !contact.is_read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <p className={`font-medium truncate ${
                          !contact.is_read ? 'text-dark-800' : 'text-dark-600'
                        }`}>
                          {contact.name}
                        </p>
                        {!contact.is_read && (
                          <span className="px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-dark-400 truncate mt-1">
                        {contact.subject || contact.message}
                      </p>
                    </div>
                    <span className="text-xs text-dark-400 ml-4">
                      {formatDate(contact.created_at)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* Quick Actions */}
        <motion.div
          className="bg-white rounded-xl shadow-sm p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <h2 className="font-semibold text-dark-800 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Link
              to="/admin/blogs/new"
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
            >
              <FaPlus className="w-6 h-6 text-primary-500 mb-2" />
              <span className="text-sm text-dark-700">New Post</span>
            </Link>
            <Link
              to="/admin/blogs"
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
            >
              <FaBlog className="w-6 h-6 text-primary-500 mb-2" />
              <span className="text-sm text-dark-700">Manage Blogs</span>
            </Link>
            <Link
              to="/admin/contacts"
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
            >
              <FaEnvelope className="w-6 h-6 text-primary-500 mb-2" />
              <span className="text-sm text-dark-700">Messages</span>
            </Link>
            <Link
              to="/admin/users"
              className="flex flex-col items-center p-4 border border-gray-200 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
            >
              <FaUsers className="w-6 h-6 text-primary-500 mb-2" />
              <span className="text-sm text-dark-700">Users</span>
            </Link>
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
